import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Building, Plus, Edit, Trash2, MapPin, Calendar, Users, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BuildingManagement = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState(null);
  const [formData, setFormData] = useState({
    building_name: '',
    building_address: '',
    latitude: '',
    longitude: '',
    building_project_id: '',
    status_id: '',
    quarter_id: '',
    task_id: '2', // Default to Task 2 (Building Projects)
    subtask_id: '1', // Default to first subtask
    cell_id: '1' // Default cell
  });

  const queryClient = useQueryClient();

  // Fetch buildings for current user
  const { data: buildings, isLoading } = useQuery(
    ['user-buildings'],
    async () => {
      const response = await axios.get('/api/buildings/user');
      return response.data;
    }
  );

  // Fetch master data
  const { data: masterData } = useQuery(
    ['building-master-data'],
    async () => {
      const response = await axios.get('/api/masters/dropdowns');
      return response.data;
    }
  );

  // Fetch building limits
  const { data: buildingLimits } = useQuery(
    ['building-limits'],
    async () => {
      const response = await axios.get('/api/buildings/limits');
      return response.data;
    }
  );

  // Add building mutation
  const addBuildingMutation = useMutation(
    async (buildingData) => {
      const response = await axios.post('/api/buildings', buildingData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-buildings']);
        queryClient.invalidateQueries(['building-limits']);
        setShowAddForm(false);
        setFormData({
          building_name: '',
          building_address: '',
          latitude: '',
          longitude: '',
          building_project_id: '',
          status_id: '',
          quarter_id: '',
          task_id: '2',
          subtask_id: '1',
          cell_id: '1'
        });
      }
    }
  );

  // Update building mutation
  const updateBuildingMutation = useMutation(
    async ({ id, ...buildingData }) => {
      const response = await axios.put(`/api/buildings/${id}`, buildingData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-buildings']);
        setEditingBuilding(null);
        setFormData({
          building_name: '',
          building_address: '',
          latitude: '',
          longitude: '',
          building_project_id: '',
          status_id: '',
          quarter_id: '',
          task_id: '2',
          subtask_id: '1',
          cell_id: '1'
        });
      }
    }
  );

  // Delete building mutation
  const deleteBuildingMutation = useMutation(
    async (id) => {
      const response = await axios.delete(`/api/buildings/${id}`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['user-buildings']);
        queryClient.invalidateQueries(['building-limits']);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingBuilding) {
      updateBuildingMutation.mutate({ id: editingBuilding.building_id, ...formData });
    } else {
      addBuildingMutation.mutate(formData);
    }
  };

  const handleEdit = (building) => {
    setEditingBuilding(building);
    setFormData({
      building_name: building.building_name,
      building_address: building.building_address,
      latitude: building.latitude,
      longitude: building.longitude,
      building_project_id: building.building_project_id,
      status_id: building.status_id,
      quarter_id: building.quarter_id,
      task_id: '2',
      subtask_id: '1',
      cell_id: '1'
    });
    setShowAddForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this building?')) {
      deleteBuildingMutation.mutate(id);
    }
  };

  // Handler functions for CSP compliance
  const handleShowAddForm = () => {
    setShowAddForm(true);
  };

  const handleFormFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingBuilding(null);
    setFormData({
      building_name: '',
      building_address: '',
      latitude: '',
      longitude: '',
      building_project_id: '',
      status_id: '',
      quarter_id: '',
      task_id: '2',
      subtask_id: '1',
      cell_id: '1'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in progress': return 'text-blue-600 bg-blue-100';
      case 'on hold': return 'text-yellow-600 bg-yellow-100';
      case 'not started': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const canAddBuilding = () => {
    if (!buildingLimits) return false;
    const currentCount = buildings?.length || 0;
    const limit = buildingLimits.max_buildings || 0;
    return currentCount < limit;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Building Management</h1>
        <button
          onClick={handleShowAddForm}
          disabled={!canAddBuilding()}
          className={`px-4 py-2 rounded-md text-white font-medium flex items-center space-x-2 ${
            canAddBuilding() 
              ? 'bg-primary-600 hover:bg-primary-700' 
              : 'bg-gray-400 cursor-not-allowed'
          }`}
        >
          <Plus className="h-4 w-4" />
          <span>Add Building</span>
        </button>
      </div>

      {/* Building Limits Info */}
      {buildingLimits && (
        <div className="card">
          <div className="flex items-center space-x-4">
            <AlertCircle className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="font-medium text-gray-900">Building Limits</h3>
              <p className="text-sm text-gray-600">
                You can add up to {buildingLimits.max_buildings} buildings across Q1-Q4 and Q5-Q8.
                Current count: {buildings?.length || 0} / {buildingLimits.max_buildings}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingBuilding ? 'Edit Building' : 'Add New Building'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.building_name}
                  onChange={(e) => handleFormFieldChange('building_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Building Address *
                </label>
                <input
                  type="text"
                  required
                  value={formData.building_address}
                  onChange={(e) => handleFormFieldChange('building_address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={(e) => handleFormFieldChange('latitude', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitude
                </label>
                <input
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={(e) => handleFormFieldChange('longitude', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Type *
                </label>
                <select
                  required
                  value={formData.building_project_id}
                  onChange={(e) => handleFormFieldChange('building_project_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Project Type</option>
                  {masterData?.buildingProjects?.map((project) => (
                    <option key={project.building_project_id} value={project.building_project_id}>
                      {project.building_project_type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  required
                  value={formData.status_id}
                  onChange={(e) => handleFormFieldChange('status_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Status</option>
                  {masterData?.buildingStatuses?.map((status) => (
                    <option key={status.building_status_id} value={status.building_status_id}>
                      {status.building_status}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quarter *
                </label>
                <select
                  required
                  value={formData.quarter_id}
                  onChange={(e) => handleFormFieldChange('quarter_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Select Quarter</option>
                  {masterData?.quarters?.map((quarter) => (
                    <option key={quarter.id} value={quarter.id}>
                      {quarter.quarter_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="submit"
                disabled={addBuildingMutation.isLoading || updateBuildingMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              >
                {editingBuilding ? 'Update Building' : 'Add Building'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Buildings List */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Your Buildings</h3>
        {buildings?.length === 0 ? (
          <div className="text-center py-8">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No buildings added yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b-2 border-gray-300 bg-gray-50">
                  <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Building Name</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Address</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Project Type</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Status</th>
                  <th className="py-3 px-4 font-semibold text-gray-700 border-r border-gray-200">Quarter</th>
                  <th className="py-3 px-4 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {buildings?.map((building) => (
                  <tr key={building.building_id} className="border-b border-gray-200">
                    <td className="py-3 px-4 border-r border-gray-200 font-medium">{building.building_name}</td>
                    <td className="py-3 px-4 border-r border-gray-200">{building.building_address}</td>
                    <td className="py-3 px-4 border-r border-gray-200">{building.project_type}</td>
                    <td className="py-3 px-4 border-r border-gray-200">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(building.status)}`}>
                        {building.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-r border-gray-200">{building.quarter_name}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(building)}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(building.building_id)}
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuildingManagement;
